/**
 * Ticket Message Form Component
 *
 * Form for sending a new message in a ticket conversation.
 * Simple textarea with send button.
 */

import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../common/Button';
import { Textarea } from '../common/Textarea';

export interface TicketMessageFormProps {
  /** Message value */
  value: string;

  /** Change handler */
  onChange: (value: string) => void;

  /** Submit handler */
  onSubmit: () => void | Promise<void>;

  /** Loading state */
  loading?: boolean;

  /** Placeholder text */
  placeholder?: string;
}

export const TicketMessageForm: React.FC<TicketMessageFormProps> = ({
  value,
  onChange,
  onSubmit,
  loading = false,
  placeholder = 'Type your message...',
}) => {
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!value || value.trim() === '') {
      setError('Please enter a message');
      return;
    }

    if (value.length > 2000) {
      setError('Message must be 2000 characters or less');
      return;
    }

    setError('');
    await onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl/Cmd + Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-neutral-200">
      <div className="space-y-3">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          error={error}
          rows={4}
          maxLength={2000}
          placeholder={placeholder}
          disabled={loading}
          helpText={`${value.length}/2000 characters • Press Ctrl/Cmd + Enter to send`}
        />

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            icon={<Send className="w-4 h-4" />}
            isLoading={loading}
            disabled={!value || value.trim() === ''}
          >
            Send Message
          </Button>
        </div>
      </div>
    </form>
  );
};
